import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Trans, useTranslation } from 'react-i18next';
import qs from 'query-string';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Checkbox, Skeleton } from '@byte-design/ui';
import { resolveUrl } from '@ad/ad_utils/src/utils/common';
import { useLoadingEffect } from '@ad/ad_utils_react/src/utils/hooks/loading';
import { sendCustomEvent, checkSuccessResponse } from '@ad/ad_utils/src';
import AgreementView from '@/components/Agreement';
import { globalInfoStore, UserRegionEnum, UserRoleEnum } from '@/models/global';
import { getRootUrl } from '@/utils';
import userApis from '@/apis/userApis';
import { AgreementID, AgreementTypeEnum } from '@/containers/BusinessRelatedParams/consts';
import { bulkWhitelistLogEventsEnum, useLogUserViewBehaviors } from '@/utils/hooks/useLogUserViewBehaviors';
import { AgreementCommonInfo, TTEntity } from '@/utils/typings';
import { withPage } from '@/utils/logger/event-tracking';
import { PageEnum } from '@/utils/logger/event-tracking/types';
import { PROJECT_PREFIX } from '@/../config/mpa-config';
import TermSwitchTab from './TermSwitchTab';
import styles from './index.module.scss';
import { reportFatal } from '@/utils/logger/slardar';
import { ErrorLabel } from '@ad/creative-business-components/src/logger/slardar/typings';
import { Agreement } from '@ad/ad_utils/src/services/ttcx/bam-auto-generate/ad.site.creative_tcpp_server_i18n/namespaces/tcpp_common';

interface StarlingKeyValuePair {
  key: string;
  default: string;
}

function AgreementPage() {
  const { t } = useTranslation();
  const { search } = useLocation();
  const navigate = useNavigate();

  const [agreement, setAgreement] = useState<Agreement>();
  const [checked, setChecked] = useState(false);
  const [agreementsRecord, setAgreementsRecord] = useState<Record<number, Agreement>>({});
  const [activeTabIndex, setActiveTabIndex] = useState(-1);
  const [unsignedAgreements, setUnsignedAgreements] = useState<AgreementCommonInfo[]>([]);
  const [agreementStarlingKey, setAgreementStarlingKey] = useState<StarlingKeyValuePair>({
    key: 'agreement_notice',
    default: 'By clicking Submit, you acknowledge and agree to <terms />.'
  });

  const isSharedFolderAgreement = useMemo(
    () => qs.parse(search)?.isSharedFolderAgreement === 'true',
    [search]
  );

  const viewBehaviorLogger = useLogUserViewBehaviors();

  async function getSOWContent(content: string, region?: string) {
    const res = await userApis.getSowInfo();
    const newContent = content || '';
    let newContentText = newContent;
    if (newContentText) {
      newContentText = newContentText!
        .replace(/\{\{\.EffectiveDateLocal\}\}/g, res.data?.effectiveDateLocal)
        .replace(/\{\{\.EffectiveDate\}\}/g, res.data?.effectiveDate)
        .replace(/\{\{\.OriginEffectiveDate\}\}/g, res.data?.originEffectiveDate)
        .replace(/\{\{\.PartnerEntityName\}\}/g, res.data?.partnerEntityName)
        .replace(/\{\{\.SignatureTable\}\}/g, '')
        .replace(/\{\{\.OrderListTable\}\}/g, '');

      if (region) {
        const regionEntityInfo = res.data?.ttEntities.find((item: TTEntity) => item.region === region);
        newContentText = newContentText!
          .replace(/\{\{\.TtEntityName\}\}/g, regionEntityInfo?.ttEntityName)
          .replace(/\{\{\.TtEntityNameLocal\}\}/g, regionEntityInfo?.ttEntityNameLocal);
      } else {
        newContentText = newContentText!
          .replace(/\{\{\.TtEntityName\}\}/g, res.data?.ttEntityName)
          .replace(/\{\{\.TtEntityNameLocal\}\}/g, res.data?.ttEntityNameLocal);
      }
    }
    return newContentText;
  }

  // 获取要签署的协议类型
  const agreementType = useMemo(() => {
    if (globalInfoStore.currentUserRole === UserRoleEnum.Advertiser) {
      return AgreementTypeEnum.Advertiser;
    }
    if (globalInfoStore.currentUserRole === UserRoleEnum.Partner) {
      return AgreementTypeEnum.Partner;
    }
    return null;
  }, [globalInfoStore.currentUserRole]);

  const [loadingList, fetchAgreementList] = useLoadingEffect(async (agreementType: AgreementTypeEnum) => {
    const unSignStatus = await userApis.getAgreementStatus(isSharedFolderAgreement);
    const unsignedAgreements: AgreementCommonInfo[] = unSignStatus?.agreements || [];

    const agreements = await userApis.getAgreementDetail({
      agreementIds: unsignedAgreements.map((item) => item.id)
    });

    // handle error report
    if (!agreements && unsignedAgreements?.length > 0) {
      reportFatal(ErrorLabel.CLIENT, {
        name: 'Api error',
        message: 'Agreement data is empty'
      });
    }

    if (agreements) {
      setAgreementsRecord(agreements);
    }
    setUnsignedAgreements(unsignedAgreements);
    if (
      unsignedAgreements.find(
        (item: AgreementCommonInfo) =>
          item.id === AgreementID.PartnerPlatformTerm || item.id === AgreementID.AdvertiserPlatformTerm
      )
    ) {
      setAgreementStarlingKey({
        key: 'agreement_notice_v2',
        default: `By clicking Submit, you acknowledge and agree to <terms /> and the <a1>TikTok for Business Commercial Terms of Service</a1>. 
        I have read and understand the <a2>Privacy Policy</a2>.`
      });
    }
  });

  // 获取当前角色的协议列表
  useEffect(() => {
    if (agreementType === null) {
      return;
    }

    fetchAgreementList(agreementType, isSharedFolderAgreement);
    viewBehaviorLogger({
      event_type: bulkWhitelistLogEventsEnum.viewTerms,
      invite_code: sessionStorage.getItem('ttcx_invite_code') as string
    });
  }, [agreementType, isSharedFolderAgreement]);

  // 设置默认展示的协议
  useEffect(() => {
    setActiveTabIndex(unsignedAgreements.length ? 0 : -1);
  }, [unsignedAgreements]);

  // 获取协议内容
  useEffect(() => {
    async function fetchData() {
      if (activeTabIndex === -1) {
        return;
      }
      const currentAgreementId = unsignedAgreements[activeTabIndex].id;
      const currentAgreementRegion = unsignedAgreements[activeTabIndex].region;
      sendCustomEvent('sign_page_view_agreement', {
        id: currentAgreementId,
        role: globalInfoStore.currentUserRole
      });

      try {
        if (
          [
            AgreementID.SOW,
            AgreementID.SubscriptionAddendumForPartner,
            AgreementID.FrameWorkSOWLATAMBR,
            AgreementID.FrameWorkSOWLATAMMX3SA,
            AgreementID.FrameWorkSOWAPACJP,
            AgreementID.FrameWorkSOWAPACKR
          ].includes(currentAgreementId) &&
          agreementsRecord[currentAgreementId]?.content
        ) {
          const newContent = { ...agreementsRecord[currentAgreementId] };
          // @ts-ignore
          newContent.content = await getSOWContent(newContent.content, currentAgreementRegion);

          setAgreement(newContent);
        } else {
          setAgreement(agreementsRecord[currentAgreementId]);
        }
      } catch (error) {
        console.error(error);
      }
    }

    fetchData();
  }, [activeTabIndex]);

  // 获取签署协议后的重定向地址
  const redirectUrl = useMemo(() => {
    const url = qs.parse(search).redirectUrl as string;
    if (url && typeof url === 'string' && url.startsWith(`${window.location.origin}${PROJECT_PREFIX}`)) {
      return url;
    }

    return resolveUrl(window.location.origin, getRootUrl('home-page'));
  }, [search]);

  const [isSigning, onSignClick] = useLoadingEffect(async () => {
    const redirect = () => {
      if (isSharedFolderAgreement) {
        navigate('/assets');
        return;
      }
      window.location.href = decodeURI(redirectUrl);
    };
    if (!unsignedAgreements || unsignedAgreements.length === 0) {
      // no terms to be signed, go to redirectUrl
      redirect();
      return;
    }
    const { code, msg } = await userApis.signAgreement({
      agreements: unsignedAgreements,
      partnerId: agreementType === AgreementTypeEnum.Partner ? globalInfoStore.currentAccountId : undefined
    });
    viewBehaviorLogger({
      event_type: bulkWhitelistLogEventsEnum.signTerms,
      invite_code: sessionStorage.getItem('ttcx_invite_code') as string
    });
    const success = checkSuccessResponse(code);
    sendCustomEvent('sign_agreement', {
      success: success ? 1 : 0,
      error_msg: msg,
      role: globalInfoStore.currentUserRole
    });
    if (!success) {
      return;
    }
    redirect();
  });

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>
        {isSharedFolderAgreement
          ? 'Welcome to TikTok Creative Shared Folder'
          : t('ttcx_greeting', 'Welcome to TikTok Creative Exchange!')}
      </h1>
      <p className={styles.subTitle}>
        {t(
          'agreement_sign_hint',
          'Before you spark your creativity, read and check ({{agreementCount}}) terms and conditions below',
          { agreementCount: unsignedAgreements.length || undefined }
        )}
      </p>
      <div className={styles.contentWrapper}>
        <TermSwitchTab value={activeTabIndex} onValueChange={setActiveTabIndex} className={styles.tabs}>
          <Skeleton loading={loadingList} avatar={false} title={false} active>
            {unsignedAgreements.map((item, index) => {
              const { agreementId, titleKey } = agreementsRecord[item.id] || {};
              const regionString = item.region ? ` - ${item.region}` : '';

              return (
                <TermSwitchTab.TabItem key={`${agreementId}${regionString}`} value={index}>
                  {`${t(titleKey || '')}${regionString}`}
                </TermSwitchTab.TabItem>
              );
            })}
          </Skeleton>
        </TermSwitchTab>
        <AgreementView loading={loadingList} agreement={agreement} className={styles.agreementContent} />
        <div className={styles.fakeRight} />
      </div>
      <div className={styles.signBar}>
        {!loadingList && (
          <>
            <Checkbox checked={checked} onValueChange={setChecked} className={styles.checkbox}>
              <Trans
                t={t}
                i18nKey={agreementStarlingKey.key}
                defaults={agreementStarlingKey.default}
                components={{
                  strong: <strong />,
                  terms: (
                    <>
                      {unsignedAgreements.map((item, index) => {
                        const { agreementId, titleKey } = agreementsRecord[item.id] || {};

                        return (
                          <span key={agreementId}>
                            <strong>{t(titleKey || '')}</strong>
                            {index < unsignedAgreements.length - 1 && ', '}
                          </span>
                        );
                      })}
                    </>
                  ),
                  a1: (
                    <a
                      href="https://ads.tiktok.com/i18n/official/policy/commercial-terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  ),
                  a2: (
                    <a
                      href="https://www.tiktok.com/legal/partner-privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                    />
                  )
                }}
                values={{
                  email: globalInfoStore.user?.email
                }}
              />
            </Checkbox>
            <Button type="primary" disabled={!checked} onClick={onSignClick} loading={isSigning}>
              {t('submit_btn')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default withPage(observer(AgreementPage), PageEnum.AgreementAssignment);
